import { PlaceOutlined } from '@mui/icons-material'
import { Autocomplete, AutocompleteProps, Box, CircularProgress } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { autocompleteAddress } from '../../async/gcloud'
import InputComponent from '../input'

// TODO: Tighten these MUI generic parameters once the component props are normalized.
type LocationAutocompleteProps = Omit<
  AutocompleteProps<string, false, false, true>,
  'freeSolo' | 'options' | 'renderInput'
>

const LocationAutocompleteInput = ({
  prompt,
  value,
  onSelected,
  onOptionSelected,
  busy,
  background,
  ...autocompleteProps
}: LocationAutocompleteProps & {
  prompt?: string
  value?: string
  // Fires on any change, including Enter and blur with free-typed text.
  onSelected?: (text: string) => void
  // Fires only when a suggestion is picked from the list.
  onOptionSelected?: (text: string) => void
  // Shows the field's spinner while the caller works on a selection (e.g.
  // saving it), alongside its own while fetching suggestions.
  busy?: boolean
  background?: string
}) => {
  const [inputValue, setInputValue] = useState(value || '')
  const [options, setOptions] = useState<string[]>([])
  const [loading, setLoading] = useState(false)

  const fetchPredictions = async (input: string) => {
    if (!input) return

    setLoading(true)
    try {
      const predictions = await autocompleteAddress(inputValue)

      setOptions(predictions)
    } catch (err) {
      console.error('Failed to fetch address predictions', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (inputValue) {
        fetchPredictions(inputValue)
      } else {
        setOptions([])
      }
    }, 50) // debounce to reduce API calls

    return () => clearTimeout(delayDebounce)
  }, [inputValue])

  return (
    <Autocomplete
      freeSolo
      options={options}
      getOptionLabel={option => option}
      value={inputValue}
      filterOptions={x => x}
      onInputChange={(_e, value) => setInputValue(value)}
      onChange={(_e, value, reason) => {
        onSelected?.(value)
        if (reason === 'selectOption' && value) {
          onOptionSelected?.(value)
        }
      }}
      onBlur={() => {
        onSelected?.(inputValue)
      }}
      renderInput={params => {
        return (
          <Box ref={params.InputProps.ref} sx={{ height: '100%', p: 0, minWidth: 'fit-content' }}>
            <InputComponent
              disableClose
              background={background}
              hasValue={Boolean(inputValue)}
              startIcon={<PlaceOutlined color="info" sx={{ fontSize: 24 }} />}
              endIcon={(loading || busy) && <CircularProgress color="inherit" size={20} />}
              sx={{ height: '100%' }}
            >
              {({ Input }) => {
                return (
                  <Input
                    {...params.inputProps}
                    placeholder={prompt || 'Your Location'}
                    sx={{
                      // emulating h4 fonts styles; need to just tie this in to the
                      // typography theme
                      fontFamily: 'Hco Gotham',
                      letterSpacing: '-0.03em',
                      minWidth: '100%',
                      lineHeight: 1.3,
                      fontSize: { xs: 15, md: 17 },
                      textOverflow: 'ellipsis',
                      padding: '16px 0px'
                    }}
                  />
                )
              }}
            </InputComponent>
          </Box>
        )
      }}
      {...autocompleteProps}
    />
  )
}

export default LocationAutocompleteInput
